/**
 * REF-2 / REF-6 — the sections of the unified Refunds page.
 *
 * They live here rather than inside `RefundsDrillPage` because two of them are consumed
 * elsewhere as well: the Sales Report's Refund Analysis card renders the SAME segmented
 * reason set (REF-6), and the component split is what makes the REF-1 arithmetic legible
 * wherever a reader is surprised that the REFUND KPI and the waterfall `Refunds` row
 * disagree.
 */
import { T, useIsDesktop } from '../reportTokens';
import KpiTooltip from '../../KpiTooltip';
import SectionEmpty from '../SectionEmpty';
import SectionError from '../SectionError';
import { fmtCurrency } from '../../drillHelpers';
import { REASON_GROUP_LABEL } from './refundReasons';
import {
    feeRefundAmount,
    REFUND_TYPE_LABEL,
    type ReasonRow,
    type RefundComponents,
    type RefundPeriodSummary,
    type RefundReasonGroup,
    type RefundRecord,
    type StaffRefundRow,
} from './refundModel';

/* ─── shared chrome ─────────────────────────────────────────────────────────── */

export function RefundCard({ title, subtitle, action, children }: {
    title: string;
    subtitle?: string;
    action?: React.ReactNode;
    children: React.ReactNode;
}) {
    const isDesktop = useIsDesktop();
    return (
        <div style={{
            background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14,
            padding: isDesktop ? '18px 20px' : '14px 14px', boxShadow: T.shadow, marginBottom: 12,
        }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: subtitle ? 2 : 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.t1 }}>{title}</div>
                {action}
            </div>
            {subtitle && <div style={{ fontSize: 11, color: T.t3, marginBottom: 12 }}>{subtitle}</div>}
            {children}
        </div>
    );
}


/* ─── REF-2 context strip ───────────────────────────────────────────────────── */

export function RefundContextStrip({ summary, periodLabel }: { summary: RefundPeriodSummary; periodLabel: string }) {
    const empty = summary.orderCount === 0;
    return (
        <div style={{ fontSize: 12, color: T.t2, marginBottom: 12, lineHeight: 1.5 }}>
            {empty ? 'No refunds in this period' : (
                <>
                    <strong style={{ color: T.t1 }}>{summary.orderCount}</strong>
                    {` order${summary.orderCount === 1 ? '' : 's'} · `}
                    <strong style={{ color: T.t1 }}>{fmtCurrency(summary.total)}</strong>
                    {' total refunded · '}
                    {periodLabel}
                </>
            )}
        </div>
    );
}

/* ─── REF-2 component split ─────────────────────────────────────────────────── */

const SEGMENTS = [
    { key: 'item', label: 'Item with tax', color: T.accent },
    { key: 'tax', label: 'Tax', color: T.blue },
    { key: 'tip', label: 'Tips', color: T.purple },
    { key: 'gratuity', label: 'Gratuity', color: T.teal },
    { key: 'fees', label: 'Fees', color: T.orange },
] as const;

/**
 * The first thing on the page (REF-2), because it is what answers the question a reader
 * arrives with: the REFUND KPI said $249.54 and the waterfall said $145.92 — where did the
 * rest go? Only the red Items segment reduces Net Sales; everything to its right is cash
 * reconciliation.
 */
export function RefundComponentSplit({ source = 'overall', split, failure, onRetry }: {
    source?: 'overall' | 'direct' | 'thirdparty';
    /** REF-1 — real data from GET /sales/live/v2/refund-component-split (`useRefundComponentSplit`). */
    split: { components: RefundComponents; total: number; itemTotal: number; emptyState: boolean };
    /**
     * No fallback on failure — the refund-log-derived vector approximates the Convenience
     * vs. Delivery Fee split from a channel-name heuristic (refundLogRow.ts), which is not
     * real data for those two components. Showing it here on a failure would silently swap
     * a real number for a guessed one, so this card shows its own retry instead, the same
     * way the Refund Log table's own fetch does.
     */
    failure: boolean;
    onRetry: () => void;
}) {
    const isDesktop = useIsDesktop();
    const c = split.components;
    const values: Record<string, number> = {
        item: c.item, tax: c.tax, tip: c.tip, gratuity: c.gratuity,
        fees: feeRefundAmount(c),
    };
    const total = split.total;
    const itemTotal = split.itemTotal;
    const empty = split.emptyState;

    // REF-1 — under the 3rd party toggle there is no component split to show: fee refunds
    // are Direct only, so a bar there would be a single Items block pretending to be a split.
    if (source === 'thirdparty') {
        return (
            <RefundCard title="Component split" subtitle="Where each refunded dollar posts in the Sales Waterfall">
                <SectionEmpty
                    kpiName="refund component"
                    size="medium"
                    minHeight={140}
                    message="Refund by Fee is Direct only. Third-party platforms return item value on their own platform and do not report a component split to Brisque."
                />
            </RefundCard>
        );
    }

    if (failure) {
        return (
            <RefundCard title="Component split" subtitle="Where each refunded dollar posts in the Sales Waterfall">
                <SectionError kpiName="refund component" onRetry={onRetry} size="medium" minHeight={140} />
            </RefundCard>
        );
    }

    const stats: Array<{ label: string; value: number; color: string; note?: string }> = [
        { label: 'Item with tax', value: c.item, color: T.accent, note: 'Reduces Net Sales' },
        { label: 'Tax', value: c.tax, color: T.blue, note: 'Cash reconciliation' },
        { label: 'Tips & Gratuity', value: Number((c.tip + c.gratuity).toFixed(2)), color: T.purple, note: 'Cash reconciliation' },
        { label: 'Fees', value: values.fees, color: T.orange, note: 'Posts to Others' },
        { label: 'Total', value: total, color: T.t1, note: 'Cash returned' },
    ];

    return (
        <RefundCard
            title="Component split"
            subtitle="Where each refunded dollar posts in the Sales Waterfall"
            action={
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <KpiTooltip
                        isMobile={!isDesktop}
                        text="Only the Item with tax component reduces Net Sales. Refunded tax, tips and gratuity net off the cash-reconciliation rows; refunded convenience and delivery fees net off Others."
                    />
                </span>
            }
        >
            {empty ? (
                <SectionEmpty kpiName="refund component" size="medium" minHeight={120} />
            ) : (
                <>
                    <div style={{ display: 'flex', height: 26, borderRadius: 7, overflow: 'hidden', background: T.hover, marginBottom: 10 }}>
                        {SEGMENTS.map((s) => {
                            const v = values[s.key] ?? 0;
                            if (v <= 0) return null;
                            return (
                                <div
                                    key={s.key}
                                    title={`${s.label} — ${fmtCurrency(v)}`}
                                    style={{ width: `${(v / total) * 100}%`, background: s.color, minWidth: 2 }}
                                />
                            );
                        })}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                        {SEGMENTS.map((s) => (
                            <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.t2 }}>
                                <span style={{ width: 9, height: 9, borderRadius: 2, background: s.color, display: 'inline-block' }} />
                                {s.label}
                            </span>
                        ))}
                    </div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isDesktop ? 'repeat(5, 1fr)' : 'repeat(2, 1fr)',
                        gap: 8,
                    }}>
                        {stats.map((s) => (
                            <div key={s.label} style={{
                                background: T.bg, border: `1px solid ${T.cardBorder}`, borderRadius: 10,
                                padding: '10px 12px',
                            }}>
                                <div style={{ fontSize: 10, fontWeight: 500, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
                                    {s.label}
                                </div>
                                <div style={{ fontSize: isDesktop ? 18 : 16, fontWeight: 700, letterSpacing: -0.4, color: s.color, fontVariantNumeric: 'tabular-nums' }}>
                                    {fmtCurrency(s.value)}
                                </div>
                                {s.note && <div style={{ fontSize: 10, color: T.t3, marginTop: 3 }}>{s.note}</div>}
                            </div>
                        ))}
                    </div>
                    {itemTotal < total && (
                        <div style={{ fontSize: 11, color: T.t3, marginTop: 12, lineHeight: 1.5 }}>
                            {fmtCurrency(total - itemTotal)} of this period&apos;s refunds returned tax, tips,
                            gratuity or fees. That cash left the till but was never revenue, so the waterfall&apos;s
                            <strong style={{ color: T.t2 }}> Refunds </strong> row reads {fmtCurrency(itemTotal)}, not {fmtCurrency(total)}.
                        </div>
                    )}
                </>
            )}
        </RefundCard>
    );
}

/* ─── card pager ────────────────────────────────────────────────────────────── */

/** What a card needs to page itself — supplied only when its rows come from a paginated endpoint. */
export interface RefundCardPagination {
    /** 1-indexed, as the API takes it. */
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    /** A page is loading; the buttons lock so a double click can't skip a page. */
    busy?: boolean;
}

/**
 * Prev / "Page x of y" / Next footer for the Reasons and Refunds-by-Staff cards. Hidden on a single
 * page, so an outlet that fits in one page sees exactly the card it saw before pagination.
 * One component for desktop and mobile — the row wraps rather than forking.
 */
function RefundCardPager({ pagination }: { pagination?: RefundCardPagination }) {
    const isDesktop = useIsDesktop();
    if (!pagination || pagination.totalPages <= 1) return null;
    const { page, totalPages, onPageChange, busy } = pagination;
    const btn = (disabled: boolean): React.CSSProperties => ({
        padding: isDesktop ? '4px 10px' : '3px 8px', borderRadius: isDesktop ? 6 : 5,
        border: `1px solid ${T.cardBorder}`, background: disabled ? T.hover : T.surface,
        color: disabled ? T.t3 : T.t1, fontSize: isDesktop ? 11 : 10, fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer',
    });
    const prevOff = busy || page <= 1;
    const nextOff = busy || page >= totalPages;
    return (
        <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'center',
            marginTop: 8, paddingTop: 10, borderTop: `1px solid ${T.divider}`,
        }}>
            <span style={{ fontSize: isDesktop ? 11 : 10, color: T.t3 }}>Page {page} of {totalPages}</span>
            <div style={{ display: 'inline-flex', gap: 6 }}>
                <button type="button" disabled={prevOff} onClick={() => onPageChange(page - 1)} style={btn(prevOff)} aria-label="Previous page">‹ Prev</button>
                <button type="button" disabled={nextOff} onClick={() => onPageChange(page + 1)} style={btn(nextOff)} aria-label="Next page">Next ›</button>
            </div>
        </div>
    );
}

/* ─── REF-6 segmented reasons ───────────────────────────────────────────────── */

const GROUP_ORDER: RefundReasonGroup[] = ['item', 'fee'];
const GROUP_COLOR: Record<RefundReasonGroup, string> = { item: T.accent, fee: T.orange };

/**
 * REF-2 — this card was mislabelled "Cancel Reasons"; cancellations are a separate concern
 * under the CANC epic and never appeared here.
 *
 * REF-6 — the two groups are rendered as separate ranked lists under their own headers,
 * never merged, so a fee-reason cluster cannot hide inside a longer quality list.
 */
export function RefundReasonsCard({ summary, reasons, pagination }: { summary: RefundPeriodSummary; reasons?: ReasonRow[]; pagination?: RefundCardPagination }) {
    // REF-6 — `reasons` overrides `summary.reasons` when a caller has a dedicated
    // reasons endpoint (the REF-2 drill-in's real /refund-reasons read); omitted, this
    // falls back to the summary derived from the refund log (the Sales Report's Refund
    // Analysis card, and the drill-in before real data lands).
    const source = reasons ?? summary.reasons;
    const groups = GROUP_ORDER.map((group) => ({
        group,
        rows: source.filter((r) => r.group === group),
    })).filter((g) => g.rows.length > 0);

    const max = Math.max(1, ...source.map((r) => r.count));

    return (
        <RefundCard title="Refund Reasons" subtitle="Item and order reasons are a service-quality signal; fee reasons are a control signal">
            {groups.length === 0 ? (
                <SectionEmpty kpiName="refund reason" size="small" minHeight={100} />
            ) : groups.map(({ group, rows }) => (
                <div key={group} style={{ marginBottom: 14 }}>
                    <div style={{
                        fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, color: T.t3,
                        textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                        <span>{REASON_GROUP_LABEL[group]}</span>
                        <span style={{ flex: 1, height: 1, background: T.divider }} />
                    </div>
                    {rows.map((r) => (
                        <div key={r.reason} style={{ marginBottom: 9 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
                                <span style={{ fontSize: 11.5, color: T.t2 }}>{r.reason}</span>
                                <span style={{ fontSize: 11.5, fontWeight: 600, color: T.t1, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                    {r.count}x · {fmtCurrency(r.amount)}
                                </span>
                            </div>
                            <div style={{ height: 6, background: T.hover, borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${Math.min((r.count / max) * 100, 100)}%`, background: GROUP_COLOR[group], borderRadius: 3, opacity: 0.75 }} />
                            </div>
                        </div>
                    ))}
                </div>
            ))}
            {/* Only when the rows came from the paginated endpoint — the summary fallback is one page. */}
            {reasons && <RefundCardPager pagination={pagination} />}
        </RefundCard>
    );
}

/* ─── REF-6 refunds by staff ────────────────────────────────────────────────── */

/**
 * REF-6 — the second line separates the two counts ("12 item · 4 fee"). Ranking stays by
 * total amount, because a single large item refund still matters more than four small fee
 * refunds; the counts are there to say WHICH kind of problem the amount represents.
 *
 * `staff` overrides `summary.staff` when a caller has the dedicated /refund-by-staff read
 * (the REF-2 drill-in's real data); omitted, this falls back to the summary derived from the
 * refund log. `tipOutExposure` always comes from `summary` — /refund-by-staff doesn't return
 * it, and the log-derived figure is the only source for it either way.
 */
export function RefundsByStaffCard({ summary, staff, pagination, rankOffset = 0 }: { summary: RefundPeriodSummary; staff?: StaffRefundRow[]; pagination?: RefundCardPagination; /** Rows on earlier pages, so ranks keep counting (page 2 of 10 starts at 11). */ rankOffset?: number }) {
    const isDesktop = useIsDesktop();
    const rows = staff ?? summary.staff;
    const max = Math.max(1, ...rows.map((r) => r.total));

    return (
        <RefundCard title="Refunds by Staff" subtitle="Staff members who processed refunds, ranked by total returned">
            {rows.length === 0 ? (
                <SectionEmpty kpiName="staff refund" size="small" minHeight={100} />
            ) : rows.map((s, i) => {
                const exposure = summary.tipOutExposure[s.staffName];
                return (
                    <div key={s.staffName} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0',
                        borderBottom: i < rows.length - 1 ? `1px solid ${T.divider}` : 'none',
                    }}>
                        <span style={{
                            width: 22, height: 22, borderRadius: 6, background: T.accentSoft, color: T.accent,
                            fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center',
                            justifyContent: 'center', flexShrink: 0,
                        }}>{rankOffset + i + 1}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 12.5, fontWeight: 600, color: T.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {s.staffName}
                                </span>
                                {exposure > 0 && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', color: T.orange }}>
                                        <KpiTooltip
                                            isMobile={!isDesktop}
                                            text={`Includes ${fmtCurrency(exposure)} in tips refunded after the tip period closed — may not match what was paid out.`}
                                        />
                                    </span>
                                )}
                            </div>
                            <div style={{ fontSize: 10.5, color: T.t3, marginTop: 2 }}>
                                {s.itemCount} item{s.itemCount === 1 ? '' : 's'} with tax
                            </div>
                            <div style={{ height: 4, background: T.hover, borderRadius: 2, overflow: 'hidden', marginTop: 5 }}>
                                <div style={{ height: '100%', width: `${Math.min((s.total / max) * 100, 100)}%`, background: T.accent, borderRadius: 2, opacity: 0.65 }} />
                            </div>
                        </div>
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: T.red, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                            {fmtCurrency(s.total)}
                        </span>
                    </div>
                );
            })}
            {/* Only when the rows came from the paginated endpoint — the summary fallback is one page. */}
            {staff && <RefundCardPager pagination={pagination} />}
        </RefundCard>
    );
}

/* ─── REF-5 refund type badge ───────────────────────────────────────────────── */

const TYPE_COLOR = { full: T.red, item: T.accent, fee: T.orange } as const;

export function RefundTypeBadge({ type }: { type: RefundRecord['type'] }) {
    return (
        <span style={{
            display: 'inline-block', padding: '1px 7px', borderRadius: 4,
            background: `${TYPE_COLOR[type]}18`, color: TYPE_COLOR[type],
            fontSize: 10, fontWeight: 700, letterSpacing: 0.2, whiteSpace: 'nowrap',
        }}>{REFUND_TYPE_LABEL[type]}</span>
    );
}
